import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  CompanyDetail,
  CompanyInput,
  ListBusinessTravelCompanies,
  UpdateTravelAgencyCompanyPayload,
  UploadFileSchema,
} from "@upclaim/sdk/src/data-contracts";

import { notify } from "@/lib/notify";
import { queryKeys } from "@/lib/query-keys";
import adminAPI from "@/services/api";

import { buildCreateCompanyPayload } from "../lib/create-company-payload";
import { type CreateCompanyFormValues, parseLegalIdentifier } from "../lib/create-company-schema";

export type BusinessTravelCompany = ListBusinessTravelCompanies[number];

/** GET /admin/business-travel-companies — the Companies page table. */
export function useBusinessTravelCompanies() {
  return useQuery({
    queryKey: queryKeys.companies.all,
    queryFn: () => adminAPI.listBusinessTravelCompanies(),
  });
}

/** PATCH /admin/business-travel-companies/{id} — inline edits, toggles, combobox. */
export function useUpdateCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      data,
      successMessage,
    }: {
      companyId: string;
      data: UpdateTravelAgencyCompanyPayload;
      successMessage?: string;
    }) =>
      notify.promise(adminAPI.updateTravelAgencyCompany(companyId, data), {
        loading: "Enregistrement…",
        success: successMessage ?? "Entreprise modifiée avec succès",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.companies.all });
    },
  });
}

/**
 * GET /admin/companies/registry-lookup — checks a SIREN/SIRET/VAT against the public
 * company registry while the admin types it. Skipped until the value can be one.
 */
export function useCompanyRegistryLookup(identifier: string) {
  return useQuery({
    queryKey: queryKeys.companies.registryLookup(identifier),
    queryFn: () => adminAPI.lookupCompanyRegistry({ identifier }),
    enabled: parseLegalIdentifier(identifier) !== null,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Creates the company the wizard describes: a full business travel company, or a
 * sub-account of an existing one when a parent company was picked.
 */
export function useCreateCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: CreateCompanyFormValues) => {
      const payload = buildCreateCompanyPayload(values);

      return notify.promise(
        payload.mode === "subAccount"
          ? adminAPI.createTravelCompanySubAccounts({ accounts: [payload.subAccount] })
          : adminAPI.createTravelAgencyCompany([payload.company]),
        {
          loading: payload.mode === "subAccount" ? "Création du sous-compte…" : "Création de l'entreprise…",
          success:
            payload.mode === "subAccount"
              ? `Sous-compte "${values.displayName}" ajouté avec succès`
              : `Entreprise "${values.displayName}" ajoutée avec succès`,
        },
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.companies.all });
    },
  });
}

/** Bulk deactivation — sequential PATCHes with disabledAt=now (parity with Vue). */
export function useDeactivateCompanies() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (companyIds: string[]) =>
      notify.promise(
        (async () => {
          for (const companyId of companyIds) {
            await adminAPI.updateTravelAgencyCompany(companyId, {
              disabledAt: new Date().toISOString(),
            });
          }
        })(),
        {
          loading: "Désactivation…",
          success: `${companyIds.length} entreprise(s) désactivée(s)`,
          error: "Erreur lors de la désactivation",
        },
      ),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.companies.all });
    },
  });
}

/** GET /admin/business-travel-companies/{id}/abandoned-claims-count — reactivation modal. */
export function useAbandonedClaimsCount(companyId: string | null) {
  return useQuery({
    queryKey: queryKeys.companies.abandonedClaimsCount(companyId ?? ""),
    queryFn: () => adminAPI.getAbandonedClaimsCountForCompany(companyId as string),
    enabled: companyId != null,
    staleTime: 0,
  });
}

export interface ReactivateCompanyInput {
  companyId: string;
  companyName: string;
  reopenAbandonedClaims: boolean;
  abandonedClaimCount: number;
}

/** Reactivation — clears disabledAt and optionally reopens abandoned claims. */
export function useReactivateCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ReactivateCompanyInput) =>
      notify.promise(
        adminAPI.updateTravelAgencyCompany(input.companyId, {
          disabledAt: null,
          reopenAbandonedClaims: input.reopenAbandonedClaims,
        }),
        {
          loading: "Réactivation…",
          success:
            input.reopenAbandonedClaims && input.abandonedClaimCount > 0
              ? `Entreprise "${input.companyName}" réactivée - ${input.abandonedClaimCount} dossier${input.abandonedClaimCount > 1 ? "s" : ""} ré-ouvert${input.abandonedClaimCount > 1 ? "s" : ""}`
              : `Entreprise "${input.companyName}" réactivée avec succès`,
          error: "Erreur lors de la réactivation",
        },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.companies.all });
    },
  });
}

/** GET /admin/companies/{id} — lazy-loaded when the CompanyModal opens. */
export function useCompanyDetail(companyId: string | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.companies.detail(companyId ?? ""),
    queryFn: async (): Promise<CompanyDetail> =>
      (await adminAPI.retrieveCompany(companyId as string)) as CompanyDetail,
    enabled: enabled && Boolean(companyId),
  });
}

/** POST upsert of the legal-entity sheet (CompanyInput). */
export function useUpsertCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CompanyInput) =>
      notify.promise(adminAPI.upsertCompany(input) as Promise<CompanyDetail>, {
        loading: "Enregistrement en cours...",
        success: "Fiche entreprise enregistrée avec succès",
      }),
    onSuccess: async (company) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.companies.all }),
        queryClient.invalidateQueries({ queryKey: queryKeys.companies.detail(company.id) }),
      ]);
    },
  });
}

/** POST /admin/companies/{id}/kbis — UploadFileSchema (DataURL payload). */
export function useUploadCompanyKbis() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ companyId, file }: { companyId: string; file: UploadFileSchema }) =>
      notify.promise(adminAPI.uploadCompanyKbis(companyId, file), {
        loading: "Envoi en cours...",
        success: "KBIS enregistré avec succès",
      }),
    onSuccess: async (_, { companyId }) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.companies.detail(companyId) });
    },
  });
}

/** GET /admin/courts — options of the "Tribunal compétent" select. */
export function useCourts(enabled: boolean) {
  return useQuery({
    queryKey: [...queryKeys.companies.all, "courts"],
    queryFn: () => adminAPI.listAllCourts(),
    enabled,
  });
}
